import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';
import { isEmpty, pick } from 'lodash-es';
import * as React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';

import API from '../../../../../lib/API';
import {
  RootStateProps,
  LearnerAndAccountRegistrationRootState,
  Verification,
} from '../../interfaces/basic';
import { trackOptimizelyCustomEvent } from '../../utils/tracker';
import {
  DisabledButton,
  Errors,
  FormLabel,
  SubmitButton,
  TextInput,
} from '../common/form';
import {
  detachUnloadHandler,
  withUnloadConfirm,
} from '../common/composer/withUnloadConfirm';
import { authenticityToken } from '.';
import { resendVerificationCode } from '../../utils/api';
import LearnerAndAccountRegistrationVerificationFormSchema from '../../validations/basic_or_coaching/learnerAndAccountRegistrationVerificationFormSchema';

interface FormValues {
  verification: Verification;
  message?: string;
  unexpected_error?: string;
}

type FormProps = RootStateProps<LearnerAndAccountRegistrationRootState>;

// TODO: Extract the common logic from LearnerAndAccountRegistrationVerificationForm and VerificationForm
const LearnerAndAccountRegistrationVerificationForm: React.FC<FormProps> = ({
  rootState,
}) => {
  const navigate = useNavigate();
  const {
    setValue,
    setError,
    getValues,
    handleSubmit,
    control,
    formState: { errors, touchedFields, isValid, isSubmitting },
  } = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      ...pick(rootState, 'verification'),
      message: '',
      unexpected_error: '',
    },
    resolver: yupResolver(
      LearnerAndAccountRegistrationVerificationFormSchema(),
    ),
  });

  const verificationFormHandleSubmit = React.useCallback(async () => {
    try {
      const response = await API.post(
        '/registration/accounts/create_learner_and_account',
        {
          ...pick(getValues(), 'verification'),
          ...pick(rootState, 'learner_account'),
          consent_id: rootState.consentId,
        },
        { 'X-CSRF-Token': authenticityToken },
      );
      const json = await response.json();

      if (response.status === 201) {
        trackOptimizelyCustomEvent('mail_auth_succeeded');
        // this event was moved from /registration/accounts/completed
        trackOptimizelyCustomEvent('trial_registration_completed');
        detachUnloadHandler();
        window.location.href = json.redirect_url;
        return;
      }

      if (response.status === 403) {
        if (json.redirect_url) {
          detachUnloadHandler();
          window.location.href = json.redirect_url;
          return;
        }
      }

      if (response.status === 400) {
        const keys = Object.keys(json.errors);
        for (const key of keys) {
          const msg = json.errors[key];
          setError(key as keyof FormValues, { type: 'api', message: msg });
        }
        return;
      }

      setValue(
        'unexpected_error',
        i18next.t('registration.sign_up.new.errors.unexpected_error'),
      );
    } catch {
      // handleSubmit内で例外が発生するとisSubmittingがfalseにならない問題があるためcatchする
      // see https://github.com/orgs/react-hook-form/discussions/10103
    }
  }, [rootState, getValues, setValue, setError]);

  if (!rootState.validatedLearnerAccount) {
    return <Navigate to="/" />;
  }

  return (
    <form
      onSubmit={handleSubmit(verificationFormHandleSubmit)}
      data-test-form-id="verification"
    >
      <div className="sm-up-size-xl xs-down-size-lg bold text-center xs-down-m-t xs-down-m-b-md sm-up-m-y-lg">
        {i18next.t('registration.sign_up.new.verification.title')}
      </div>
      <div className="row sm-up-m-b-xl xs-down-m-b-md">
        <div className="col-sm-10 col-sm-offset-1 col-lg-8 col-lg-offset-2">
          {i18next.t('registration.sign_up.new.verification.lead')}
        </div>
      </div>

      <div className="row m-b-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <FormLabel
            errors={errors}
            getValues={getValues}
            label={i18next.t('register.form.label.verification_code')}
            fieldNames="verification.code"
          />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Controller
            control={control}
            name="verification.code"
            render={({
              field: { onChange, onBlur, value, name },
              fieldState: { error },
            }) => (
              <TextInput
                error={error}
                value={value}
                fieldName={name}
                onChange={onChange}
                onBlur={onBlur}
                autoFocus={isEmpty(value)}
                onKeyUp={(e) =>
                  setValue(name, e.target.value.trim().toLowerCase(), {
                    shouldValidate: true,
                  })
                }
              />
            )}
          />
          <Errors
            errors={errors}
            touchedFields={touchedFields}
            fieldNames="verification.code"
          />
        </div>
      </div>

      <div className="row sm-up-m-b-xl xs-down-m-b-lg">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          {isValid ? (
            <SubmitButton
              isSubmitting={isSubmitting}
              label={i18next.t('registration.sign_up.new.verification.submit')}
              buttonClassNames="ytm-new_certification"
            />
          ) : (
            <DisabledButton
              label={i18next.t(
                'registration.sign_up.new.verification.not_ready_to_submit',
              )}
              buttonClassNames="p-y-md"
            />
          )}
        </div>
      </div>

      <div className="row sm-up-m-b-xl xs-down-m-b">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 size-sm">
          {i18next.t('registration.sign_up.new.verification.followup')}
        </div>
      </div>

      <div className="row m-x-0">
        <div className="col-sm-4 col-sm-offset-4 p-x-lg">
          {getValues('message') && (
            <div className="alert alert-success text-center">
              {getValues('message')}
            </div>
          )}
          {getValues('unexpected_error') && (
            <div className="alert alert-danger">
              {getValues('unexpected_error')}
            </div>
          )}
        </div>
      </div>

      <div className="row xs-down-m-t-md sm-up-m-b-lg xs-down-m-b back-buttons">
        <div className="col-sm-4 col-sm-offset-4 xs-down-m-b-md">
          <div
            className="btn btn-secondary btn-block ytm-new_mail_resend"
            id="send-verification-code"
            onClick={() =>
              resendVerificationCode(
                rootState.learner_account.email,
                rootState.learner_account.grade_id,
                rootState.gradeCode,
                setValue,
              )
            }
          >
            {i18next.t(
              'registration.sign_up.new.verification.resend_verification_code.submit',
            )}
          </div>
        </div>
        <div className="col-sm-2 col-sm-pull-8 col-xs-12 sm-up-m-l-lg">
          <div className="back-link">
            <a className="primary bold" onClick={() => navigate(-1)}>
              <span className="back-icon">
                <svg className="icon">
                  <use xlinkHref="#common-back" />
                </svg>
              </span>
              {i18next.t('registration.sign_up.new.verification.back')}
            </a>
          </div>
        </div>
      </div>
    </form>
  );
};

export default withUnloadConfirm(LearnerAndAccountRegistrationVerificationForm);
